import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { MapPin, ChevronDown } from "lucide-react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { getalltriplist, getvehicles, getAllVehicles } from "../../../../Services/EndPoints";
import LoadingOverlay from "../../ExpectedTrackForm/LoadingOverlay";
import Table from "../../../../Components/UiComponents/Table/Table";
import { getData, postDataWithBody } from "../../../../Services/Services";

const VEHICLE_EMOJI_MAP = {
    car: '🚗', sedan: '🚗',
    bus: '🚌', minibus: '🚌',
    truck: '🚛', lorry: '🚛',
    bike: '🏍️', bicycle: '🚲', motorcycle: '🏍️', motorbike: '🏍️',
    van: '🚐',
    suv: '🚙',
    autorickshaw: '🛺', auto: '🛺', rickshaw: '🛺',
    ambulance: '🚑',
    firetruck: '🚒',
    police: '🚔',
    taxi: '🚕',
    tractor: '🚜',
};
const getVehicleEmoji = (type) => VEHICLE_EMOJI_MAP[(type || '').toLowerCase().replace(/[\s_-]+/g, '')] || '🚘';

const TripTracker = () => {

    const [vehicleTrackingData, setVehicleTrackingData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [formData, setFormData] = useState({
        vehicleId: null
    });

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchVehicles();
    }, []);
    
    const fetchVehicles = async () => {
        try {
            setIsLoading(true);
            // const response = await getData(getvehicles);
            const response = await getData(getAllVehicles);
            if (Array.isArray(response)) {
                setVehicles(response.map((v) => ({
                    id: v.id,
                    vehicleNumber:      v.vehicle_number,
                    vehicleName:        v.vehicle_number,
                    vehicleType:        v.vehicle_type        || '',
                    brand:              v.brand               || '',
                    model:              v.model               || '',
                    fuelType:           v.fuel_type           || '',
                    color:              v.color               || '',
                    registrationNumber: v.registration_number || '',
                    driverName:         v.driver_name         || '',
                    driverPhone:        v.driver_phone        || '',
                    driverLicense:      v.driver_license      || '',
                    licenseExpiry:      v.license_expiry      || '',
                })));
            }
        } catch (error) {
            console.error('Error fetching vehicles:', error);
            toast.error('Failed to fetch vehicles');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVehicleSelect = (vehicleId) => {
        setFormData({
            vehicleId: vehicleId
        });
        setIsDropdownOpen(false);
        setSearchTerm('');
    };

    const filteredVehicles = vehicles.filter(vehicle =>
        vehicle.vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const validateForm = () => {
        if (!formData.vehicleId) {
            toast.error('Please select a vehicle', {
                position: "top-center"
            });
            return false;
        }
        return true;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Function to get current date formatted for API
    const getCurrentDateForAPI = (isEndDate = false) => {
        const date = new Date();
      //  date.setDate(date.getDate() - 2)

        const pad = (n) => String(n).padStart(2, "0");

        if (isEndDate) {
            date.setHours(23, 59, 59);
        } else {
            date.setHours(0, 0, 0);
        }

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
            `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Vehicle Number",
                accessor: "vehicleNumber",
            },
            {
                Header: "Route Name",
                accessor: "routeName",
                disableSortBy: true,
            },
            {
                Header: () => <span>Planned Start<br/>Date & Time</span>,
                accessor: "startDate",
                Cell: ({ value }) => formatDateWithoutSeconds(value)
            },
            {
                Header: () => <span>Planned End<br/>Date & Time</span>,
                accessor: "endDate",
                disableSortBy: true,
                Cell: ({ value }) => formatDateWithoutSeconds(value)
            },
            {
                Header: () => <span>Travelled Start<br/>Date & Time</span>,
                accessor: "actualStartDate",
                Cell: ({ value }) => value ? formatDateWithoutSeconds(value) : "N/A"
            },
            {
                Header: () => <span>Travelled End<br/>Date & Time</span>,
                accessor: "actualEndDate",
                disableSortBy: true,
                Cell: ({ value }) => value ? formatDateWithoutSeconds(value) : "N/A"
            },
            {
                Header: "Status",
                accessor: "status",
                disableSortBy: true,
                Cell: ({ value }) => {
                    const displayText = value === 'TRIP_TERMINATED_INCIDENT'
                        ? 'Trip Terminated - Incident'
                        : (value || 'Unknown').replace(/_/g, ' ').toLowerCase()
                            .replace(/\b\w/g, c => c.toUpperCase());

                    let color = '#1a7a73';
                    switch (value) {
                        case 'TRIP_TERMINATED_INCIDENT': color = '#dc3545'; break;
                        case 'IN_PROGRESS':              color = '#f59e0b'; break;
                        case 'TRAVELED':                 color = '#28a745'; break;
                        case 'NOT_YET_TRAVELED':         color = '#dc3545'; break;
                        default: break;
                    }

                    return (
                        <span style={{
                            backgroundColor: `${color}18`,
                            color: color,
                            border: `1.5px solid ${color}55`,
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            whiteSpace: 'nowrap',
                        }}>
                            <span style={{
                                width: '6px', height: '6px', borderRadius: '50%',
                                backgroundColor: color, flexShrink: 0,
                                display: 'inline-block',
                            }}></span>
                            {displayText}
                        </span>
                    );
                }
            },
            {
                Header: t("Action"),
                disableSortBy: true,
                Cell: ({ row }) => (
                    <button
                        onClick={() => {
                            const rowNum = (row.original.vehicleNumber || '').trim().toLowerCase();
                            const match  = vehicles.find(v => (v.vehicleNumber || '').trim().toLowerCase() === rowNum);
                            navigate("/RoutePlanning/trip-trakingView", {
                                state: {
                                    data: row.original,
                                    edit: true,
                                    vehicleType: match?.vehicleType || '',
                                    vehicleData: match || null,
                                },
                            });
                        }}
                        title="Track Trip"
                        style={{
                            backgroundColor: '#0f4a45',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 11px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        <MapPin size={12} />
                        Track
                    </button>
                ),
            },
        ],
        [navigate, vehicles]
    );
    
    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsSubmitLoading(true);
        setIsLoading(true);
        try {
            // Get selected vehicle number
            const selectedVehicle = vehicles.find(v => v.id === formData.vehicleId);
            
            if (!selectedVehicle) {
                toast.error('Selected vehicle not found');
                return;
            }

            // Prepare request body with today's date
            const requestBody = {
                vehicleNumbers: [selectedVehicle.vehicleNumber],
                startDate: getCurrentDateForAPI(false), // Today 00:00:00
                endDate: getCurrentDateForAPI(true)     // Today 23:59:59
            };

            console.log("Request Body:", requestBody);

            // Make POST request
            const response = await postDataWithBody(getalltriplist, requestBody, {});
            console.log("Response:", response);

            if (response) {
                if (response.status === 'success') {
                    toast.success(response.message, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setVehicleTrackingData(response.data || []);
                    setRowCount((response.data && response.data.length) || 0);
                } else {
                    toast.error(response.message || 'Failed to fetch planned routes', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setVehicleTrackingData([]);
                    setRowCount(0);
                }
            } else {
                toast.info('No planned routes found for selected vehicle');
                setVehicleTrackingData([]);
                setRowCount(0);
            }
        } catch (error) {
            console.error('Error fetching planned routes:', error);
            toast.error('Failed to fetch planned routes. Please try again.');
            setVehicleTrackingData([]);
            setRowCount(0);
        } finally {
            setIsLoading(false);
            setIsSubmitLoading(false);
        }
    };

    const formatDateWithoutSeconds = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
    };

    const selectedVehicle = vehicles.find(v => v.id === formData.vehicleId);

    const filterLabelStyle = {
        fontSize: '11px', fontWeight: '600', color: '#4a5568',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '5px', display: 'block',
    };

    return (
        <div className="container-fluid" style={{ paddingTop: '4px' }}>
            {isSubmitLoading && <LoadingOverlay message="Processing..." />}

            {/* ── Filter Bar ── */}
            <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #0f4a45',
                borderRadius: '10px',
                padding: '8px 14px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'nowrap',
            }}>
                {/* Inline label */}
                <label style={{ ...filterLabelStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Vehicle Number <span style={{ color: '#dc3545' }}>*</span>
                </label>

                {/* Vehicle dropdown */}
                <div style={{ flex: '0 0 260px' }}>
                    <div style={{ position: 'relative' }} ref={dropdownRef}>
                        <div
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            style={{
                                height: '35px',
                                padding: '0 34px 0 10px',
                                border: `1px solid ${isDropdownOpen ? '#0f4a45' : '#dee2e6'}`,
                                borderRadius: '7px',
                                background: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: selectedVehicle ? '#333' : '#adb5bd',
                                boxShadow: isDropdownOpen ? '0 0 0 0.18rem rgba(26,122,115,0.15)' : 'none',
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                userSelect: 'none',
                            }}
                        >
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {selectedVehicle
                                    ? <><span style={{ fontSize: '15px', lineHeight: 1 }}>{getVehicleEmoji(selectedVehicle.vehicleType)}</span>{selectedVehicle.vehicleNumber}</>
                                    : 'Select Vehicle Number'}
                            </span>
                            <ChevronDown
                                size={14}
                                style={{
                                    position: 'absolute', right: '10px', top: '50%',
                                    transform: `translateY(-50%) rotate(${isDropdownOpen ? '180deg' : '0deg'})`,
                                    color: '#6c757d', transition: 'transform 0.2s', flexShrink: 0,
                                }}
                            />
                        </div>

                        {isDropdownOpen && (
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 4px)', left: 0, right: 0,
                                background: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '8px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                zIndex: 1050,
                                overflow: 'hidden',
                            }}>
                                <div style={{ padding: '7px', borderBottom: '1px solid #f0f4f8' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="Search vehicles..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                width: '100%', height: '32px',
                                                padding: '0 30px 0 10px',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                        <i className="fas fa-search" style={{
                                            position: 'absolute', right: '10px', top: '50%',
                                            transform: 'translateY(-50%)', color: '#0f4a45',
                                            pointerEvents: 'none', fontSize: '11px',
                                        }}></i>
                                    </div>
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {filteredVehicles.length === 0 ? (
                                        <div style={{ padding: '14px', textAlign: 'center', color: '#6c757d', fontSize: '12px' }}>
                                            No vehicles found
                                        </div>
                                    ) : (
                                        filteredVehicles.map(vehicle => (
                                            <div
                                                key={vehicle.id}
                                                onClick={() => handleVehicleSelect(vehicle.id)}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    color: '#333',
                                                    background: formData.vehicleId === vehicle.id ? '#e8f4fd' : 'transparent',
                                                    borderLeft: formData.vehicleId === vehicle.id ? '3px solid #0f4a45' : '3px solid transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                }}
                                            >
                                                <span style={{ fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>
                                                    {getVehicleEmoji(vehicle.vehicleType)}
                                                </span>
                                                {vehicle.vehicleNumber}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search button */}
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading || !formData.vehicleId}
                    style={{
                        flexShrink: 0,
                        height: '35px',
                        padding: '0 16px',
                        border: 'none',
                        borderRadius: '7px',
                        background: isLoading || !formData.vehicleId
                            ? '#adb5bd'
                            : 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: isLoading || !formData.vehicleId ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap',
                        boxShadow: isLoading || !formData.vehicleId ? 'none' : '0 2px 6px rgba(26,122,115,0.28)',
                    }}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"
                                style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                            Loading...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-search" style={{ fontSize: '11px' }}></i>
                            Search Today's Trips
                        </>
                    )}
                </button>
            </div>

            {/* ── Table ── */}
            <div className="datatable__container trip-tracker-table">
                <Table
                    data={vehicleTrackingData}
                    title=""
                    showFilter={false}
                    columns={columns}
                    numberOfRows={itemsPerPage}
                    showExport={false}
                    showReset={false}
                    onExpand={false}
                    showSearch={true}
                    isHeaderFixed={true}
                    rowCount={rowCount}
                    headerTableHeadStyle={{
                        background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)",
                        color: "#FFFFFF",
                    }}
                    
                />
            </div>

            <style>{`
                .grid-stack>.grid-stack-item>.grid-stack-item-content {
                    margin: 0;
                    position: absolute;
                    width: auto;
                    overflow-y: hidden;
                }

                .datatable__container.trip-tracker-table {
                    overflow: visible;
                    margin-bottom: 6px;
                }

                /* ── Table header ── */
                .trip-tracker-table .iccc--table thead th {
                    padding: 13px 16px !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    letter-spacing: 0.7px !important;
                    text-transform: uppercase !important;
                    border: none !important;
                    white-space: nowrap !important;
                    border-bottom: 2px solid rgba(255,255,255,0.15) !important;
                    background: linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%) !important;
                }

                /* ── Remove all row/cell borders ── */
                .trip-tracker-table .iccc--table tbody tr,
                .trip-tracker-table .iccc--table tbody td {
                    border: none !important;
                    border-color: transparent !important;
                }
                .trip-tracker-table .table > :not(caption) > * > * {
                    border-bottom-width: 0 !important;
                }

                /* ── Prevent column wrapping ── */
                .trip-tracker-table .iccc--table thead th,
                .trip-tracker-table .iccc--table tbody td {
                    white-space: nowrap !important;
                }

                /* ── Body cell padding ── */
                .trip-tracker-table .iccc--table tbody td {
                    padding: 10px 16px !important;
                    vertical-align: middle !important;
                    font-size: 13px !important;
                }

                /* ── Zebra striping ── */
                .trip-tracker-table .iccc--table tbody tr:nth-child(odd) td {
                    background-color: #ffffff !important;
                }
                .trip-tracker-table .iccc--table tbody tr:nth-child(even) td {
                    background-color: #f4f8fd !important;
                }

                /* ── Row hover ── */
                .trip-tracker-table .iccc--table tbody tr:hover td {
                    background-color: #e3eef9 !important;
                    transition: background-color 0.12s ease;
                }
                .trip-tracker-table .iccc--table tbody tr:hover td:first-child {
                    box-shadow: inset 3px 0 0 #0f4a45 !important;
                }

                /* ── Table header toolbar ── */
                .trip-tracker-table .iccc__table-header {
                    padding: 8px 4px !important;
                    // border-bottom: 1px solid #e8ecf0 !important;
                }

                .trip-tracker-table .iccc__table--container {
                    padding: 0px !important;
                    border-radius: 6px !important;
                    margin: 0px !important;
                    // border-bottom: 1px solid #e8ecf0 !important;
                }

                /* ── Dropdown hover for vehicle list ── */
                .trip-tracker-vehicle-option:hover {
                    background-color: #f0f7ff !important;
                }
            `}</style>
        </div>
    );
};

export default TripTracker;